import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { v4 as uuid } from 'uuid';

import {
  Field,
  VisibleFieldsForPartResource,
} from '®app/resource/edit/visible-fields';
import {
  ALL_BIBLIOGRAPHIC_RESOURCE_TYPES,
  BibliographicResource,
  BibliographicResourceType,
  STABLE_BIBLIOGRAPHIC_RESOURCE_TYPES,
} from '®shared/bibliographic-resource';
import { AppConfig } from '®shared/config';

@Component({
  selector: 'rl-resource-edit-form',
  styleUrls: ['./resource-edit-form.component.scss'],
  templateUrl: './resource-edit-form.component.html',
})
export class ResourceEditFormComponent implements OnInit, OnChanges {
  @Input() resource: BibliographicResource;
  @Input() visibleFields: VisibleFieldsForPartResource;

  constructor(private appConfig: AppConfig, private formBuilder: FormBuilder) {}

  public form: FormGroup;
  public id = uuid();

  public readonly resourceTypes: BibliographicResourceType[] = this.appConfig
    .isUnstableResourceTypesEnabled
    ? ALL_BIBLIOGRAPHIC_RESOURCE_TYPES
    : STABLE_BIBLIOGRAPHIC_RESOURCE_TYPES;

  ngOnInit(): void {
    this.form = this.createForm(this.resource);
  }

  ngOnChanges(): void {
    this.form = this.createForm(this.resource);
  }

  addIterableField(field: Field): void {
    const formArray = this.form.get(field.name) as FormArray;

    if (field.isIterable) {
      if (this.hasContributors(field)) {
        formArray.push(
          this.formBuilder.group({
            full_name: [''],
            first_name: [''],
            surname: [''],
          }),
        );
      } else {
        let individualFieldName: string = null;
        switch (field.name) {
          case 'optionalIsbn13s':
            individualFieldName = 'isbn13';
            break;
          case 'optionalIsbn10s':
            individualFieldName = 'isbn10';
            break;
          case 'issns':
            individualFieldName = 'issn';
            break;
          case 'eissns':
            individualFieldName = 'eissn';
            break;
          case 'dates':
            individualFieldName = 'date';
            break;
          case 'publisher_names':
            individualFieldName = 'publisherName';
            break;
          case 'places_of_publication':
            individualFieldName = 'placeOfPublication';
            break;
          case 'web_addresses':
            individualFieldName = 'webAddress';
            break;
        }

        if (individualFieldName) {
          formArray.push(
            this.formBuilder.group({ [individualFieldName]: [''] }),
          );
        }
      }
    }
  }

  get authors(): FormArray {
    return this.getFormArray('authors');
  }

  get editors(): FormArray {
    return this.getFormArray('editors');
  }

  get dates(): FormArray {
    return this.getFormArray('dates');
  }

  get optionalIsbn10s(): FormArray {
    return this.getFormArray('optionalIsbn10s');
  }

  get optionalIsbn13s(): FormArray {
    return this.getFormArray('optionalIsbn13s');
  }

  get issns(): FormArray {
    return this.getFormArray('issns');
  }

  get eissns(): FormArray {
    return this.form.get('eissns') as FormArray;
  }

  get publisher_names(): FormArray {
    return this.getFormArray('publisher_names');
  }

  get places_of_publication(): FormArray {
    return this.getFormArray('places_of_publication');
  }

  get web_addresses(): FormArray {
    return this.getFormArray('web_addresses');
  }

  hasContributors(field: Field): boolean {
    return ['authors', 'editors'].includes(field.name);
  }

  private getFormArray(fieldName: string): FormArray {
    const field = this.form.get(fieldName);
    if (!(field instanceof FormArray)) {
      throw Error(`Field is not a 'FormArray': ${fieldName}`);
    }

    return field;
  }

  private createForm(bibliographicResource: BibliographicResource): FormGroup {
    const formFields = {
      resource_type: [bibliographicResource.resource_type, Validators.required],
      title: [bibliographicResource.title, Validators.required],
      lcn: [bibliographicResource.lcn],
      doi: [bibliographicResource.doi],
      volume: [bibliographicResource.volume],
      issue: [bibliographicResource.issue],
      page_start: [bibliographicResource.page_start],
      page_end: [bibliographicResource.page_end],
      edition: [bibliographicResource.edition],
      format: [bibliographicResource.format],
      location: [bibliographicResource.location],
      court: [bibliographicResource.court],
      series: [bibliographicResource.series],
      number: [bibliographicResource.number],
      source_uri: [bibliographicResource.source_uri],
      identifier: [bibliographicResource.identifier],
      authors: this.formBuilder.array([]),
      editors: this.formBuilder.array([]),
      optionalIsbn13s: this.formBuilder.array([]),
      optionalIsbn10s: this.formBuilder.array([]),
      issns: this.formBuilder.array([]),
      eissns: this.formBuilder.array([]),
      dates: this.formBuilder.array([]),
      publisher_names: this.formBuilder.array([]),
      places_of_publication: this.formBuilder.array([]),
      web_addresses: this.formBuilder.array([]),
    };

    if (this.resource.online_resource) {
      formFields['online_resource'] = this.formBuilder.group({
        source: [this.resource.online_resource.source],
        link: [this.resource.online_resource.link],
      });
    }

    bibliographicResource.authors?.forEach(author => {
      formFields.authors.push(
        this.formBuilder.group({
          full_name: [author.full_name],
          first_name: [author.first_name],
          surname: [author.surname],
          type: [author.type],
        }),
      );
    });

    bibliographicResource.editors?.forEach(editor => {
      formFields.editors.push(
        this.formBuilder.group({
          full_name: [editor.full_name],
          first_name: [editor.first_name],
          surname: [editor.surname],
          type: [editor.type],
        }),
      );
    });

    bibliographicResource.optionalIsbn13s?.forEach(isbn13 => {
      formFields.optionalIsbn13s.push(
        this.formBuilder.group({
          isbn13: [isbn13],
        }),
      );
    });

    bibliographicResource.optionalIsbn10s?.forEach(isbn10 => {
      formFields.optionalIsbn10s.push(
        this.formBuilder.group({
          isbn10: [isbn10],
        }),
      );
    });

    bibliographicResource.issns?.forEach(issn => {
      formFields.issns.push(
        this.formBuilder.group({
          issn: [issn],
        }),
      );
    });

    bibliographicResource.eissns?.forEach(eissn => {
      formFields.eissns.push(
        this.formBuilder.group({
          eissn: [eissn],
        }),
      );
    });

    bibliographicResource.dates?.forEach(date => {
      formFields.dates.push(
        this.formBuilder.group({
          date: [date],
        }),
      );
    });

    bibliographicResource.publisher_names?.forEach(publisherName => {
      formFields.publisher_names.push(
        this.formBuilder.group({
          publisherName: [publisherName],
        }),
      );
    });

    bibliographicResource.places_of_publication?.forEach(placeOfPublication => {
      formFields.places_of_publication.push(
        this.formBuilder.group({
          placeOfPublication: [placeOfPublication],
        }),
      );
    });

    bibliographicResource.web_addresses?.forEach(webAddress => {
      formFields.web_addresses.push(
        this.formBuilder.group({
          webAddress: [webAddress],
        }),
      );
    });

    return this.formBuilder.group(formFields);
  }
}
